import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {Box, Flex, IconButton, Text} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import useDetectDevice from '../../../hooks/use-detect-device'
import {preferenceToBool, useSitePreferences} from '../../../hooks/use-site-preferences'
import {useBrand} from '../../../contexts/brand-context'
import WebDrawer from '../../../components/web-drawer'
import {WalletAppleIcon, WalletGoogleIcon} from '../../../components/icons'
import BarcodeComponent from '../../../components/barcode-component'

const CollectifIdModal = ({isOpen, onClose, memberNumber}) => {
    const {formatMessage} = useIntl()
    const [isLoading, setIsLoading] = useState(false)
    const {isIOS, isAndroid} = useDetectDevice()
    const {data: sitePrefs} = useSitePreferences(['googleWalletEnabled', 'appleWalletEnabled'])
    const {site, locale} = useMultiSite()
    const {brandId} = useBrand()
    const {getTokenWhenReady} = useAccessToken()
    const {app: appConfig} = getConfig()

    const hasGoogleWalletPreference = Object.prototype.hasOwnProperty.call(
        sitePrefs || {},
        'googleWalletEnabled'
    )
    const hasAppleWalletPreference = Object.prototype.hasOwnProperty.call(
        sitePrefs || {},
        'appleWalletEnabled'
    )

    const isGoogleWalletEnabled = hasGoogleWalletPreference
        ? preferenceToBool(sitePrefs?.googleWalletEnabled)
        : true
    const isAppleWalletEnabled = hasAppleWalletPreference
        ? preferenceToBool(sitePrefs?.appleWalletEnabled)
        : true

    const shouldShowMobileWalletButton =
        (isIOS && isAppleWalletEnabled) || (isAndroid && isGoogleWalletEnabled)

    const handleMobileWalletClick = async () => {
        if (isLoading || (!isAndroid && !isIOS)) {
            return
        }

        const organizationId = appConfig?.commerceAPI?.parameters?.organizationId
        const fallbackSiteId = appConfig?.commerceAPI?.parameters?.siteId
        const siteId = site?.id || fallbackSiteId
        const localeId = locale?.id
        const cBrand = brandId === 'dynamite' ? 'dynamite' : 'garage'

        if (!organizationId || !siteId || !localeId) {
            return
        }

        setIsLoading(true)
        try {
            const token = await getTokenWhenReady()
            const proxyPath = appConfig?.commerceAPI?.proxyPath
            const endpoint = isIOS ? 'apple' : 'google'
            const url = `${proxyPath}/custom/mobile-wallet/v1/organizations/${organizationId}/${endpoint}?siteId=${encodeURIComponent(
                siteId
            )}&locale=${encodeURIComponent(localeId)}&c_brand=${encodeURIComponent(cBrand)}`

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                }
            })

            if (!response.ok) {
                return
            }

            const responseData = await response.json()
            if (responseData?.fileUrl) {
                window.location.assign(responseData.fileUrl)
            }
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <Box display={{base: 'block', lg: 'none'}}>
            <WebDrawer isOpen={isOpen} onClose={onClose} size="85vh">
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                >
                    <Text as="h2" textStyle="webH2" mb={6}>
                        {formatMessage({
                            id: 'account.collectif.modal.points.title',
                            defaultMessage: "Hey! Here's your Garage Collectif ID Code!"
                        })}
                    </Text>
                    <Text as="p" textStyle="bodySmRegular" mb={8}>
                        {formatMessage({
                            id: 'account.collectif.modal.points.description',
                            defaultMessage:
                                'Present your ID Code at the cash to link your purchase to your Collectif account & earn points to your purchase!'
                        })}
                    </Text>
                    
                    {/* Barcode */}
                    {memberNumber && <BarcodeComponent value={memberNumber} collapsed={false} hideLabel={true} idBottom={true} />}
                </Box>

                {/* Wallet button */}
                {shouldShowMobileWalletButton && (
                    <Flex alignItems="flex-end" justifyContent="center" height="33vh">
                        <IconButton
                            variant="unstyled"
                            aria-label={isIOS
                                ? formatMessage({id: 'account.collectif.modal.wallet.apple', defaultMessage: 'Add to Apple Wallet'})
                                : formatMessage({id: 'account.collectif.modal.wallet.google', defaultMessage: 'Add to Google Wallet'})
                            }
                            icon={
                                isIOS ? (
                                    <WalletAppleIcon boxSize={'155px'} />
                                ) : (
                                    <WalletGoogleIcon boxSize={'174px'} />
                                )
                            }
                            onClick={handleMobileWalletClick}
                            isLoading={isLoading}
                            isDisabled={isLoading}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            w={isIOS ? '155px' : '174px'}
                            h="auto"
                            mb={12}
                        />
                    </Flex>
                )}
            </WebDrawer>
        </Box>
    )
}

CollectifIdModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    memberNumber: PropTypes.string
}

export default CollectifIdModal
